'use client';

import type { ReactNode } from 'react';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { getUserHasActiveSubscriptions } from '@libs/analytics';
import { logSentryError, SentryAcpAutoshipErrors as SentryErrors, useUserSession } from '@libs/common-next';

import { readCache, writeCache } from './utils';

interface AutoshipDataContextValue {
    ogMerchantUserId?: string;
    hasActiveSubscriptions?: boolean;
    subscriptions?: unknown[];
    count?: number;
    isLoading: boolean;
    refresh: () => Promise<void>;
}

export const AutoshipDataContext = createContext<AutoshipDataContextValue | null>(null);

export function AutoshipDataProvider({ children }: Readonly<{ children: ReactNode }>) {
    const { user } = useUserSession();

    const [ogMerchantUserId, setOgMerchantUserId] = useState<string | undefined>(undefined);
    const [hasActiveSubscriptions, setHasActiveSubscriptions] = useState<boolean | undefined>(undefined);
    const [subscriptions, setSubscriptions] = useState<unknown[] | undefined>(undefined);
    const [count, setCount] = useState<number | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const applyData = (data: {
        ogMerchantUserId?: string;
        hasActiveSubscriptions?: boolean;
        subscriptions?: unknown[];
        count?: number;
    }) => {
        setOgMerchantUserId(data.ogMerchantUserId);
        setHasActiveSubscriptions(data.hasActiveSubscriptions);
        setCount(data.count);
        setSubscriptions(data.subscriptions);
    };

    const fetchAndCache = useCallback(async () => {
        if (!user?.sid) return;
        setIsLoading(true);

        try {
            const cachedData = readCache();

            if (cachedData) {
                applyData(cachedData);
            }

            const response = await getUserHasActiveSubscriptions(user.sid);

            const resCount = response?.count ?? 0;
            const resHasSubscriptions = response?.hasAutoshipOrders ?? false;
            const resSubscriptions = response?.results ?? [];

            const newData = {
                ogMerchantUserId: user.sid,
                hasActiveSubscriptions: resHasSubscriptions,
                subscriptions: resSubscriptions,
                count: resCount,
            };

            const isSame =
                cachedData &&
                cachedData.ogMerchantUserId === newData.ogMerchantUserId &&
                cachedData.hasActiveSubscriptions === newData.hasActiveSubscriptions &&
                cachedData.count === newData.count &&
                JSON.stringify(cachedData.subscriptions) === JSON.stringify(newData.subscriptions);

            if (!isSame) {
                applyData(newData);
                if (user.sid.trim() !== '') {
                    writeCache(newData);
                }
            }
        } catch (error) {
            logSentryError(error, SentryErrors.AutoshipPage.LoadOrdergrooveScriptFailed);
        } finally {
            setIsLoading(false);
        }
    }, [user?.sid]);

    useEffect(() => {
        if (!user?.sid) return;
        void fetchAndCache();
    }, [fetchAndCache, user?.sid]);

    const refresh = useCallback(async () => {
        await fetchAndCache();
    }, [fetchAndCache]);

    const value = useMemo<AutoshipDataContextValue>(
        () => ({
            ogMerchantUserId,
            hasActiveSubscriptions,
            subscriptions,
            count,
            isLoading,
            refresh,
        }),
        [ogMerchantUserId, hasActiveSubscriptions, subscriptions, count, isLoading, refresh]
    );

    return <AutoshipDataContext.Provider value={value}>{children}</AutoshipDataContext.Provider>;
}
